import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Skeleton,
  Chip,
  CircularProgress,
  Button
} from '@mui/material';
import { Close, CalendarToday, DoneAll, FiberNew } from '@mui/icons-material';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { UniversalPagination } from '@/components/common/UniversalPagination';
import { DashboardLayout } from '@/components/DashboardLayout';
import { newsService } from '@/services/news.service';
import { PublicNewsItem } from '@/types/news';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { getImageUrl } from '@/utils/image-url.util';
import { useAppDispatch } from '@/store';
import { decrementUnreadCount, markAllNewsAsRead } from '@/store/news/newsSlice';
import { decrementBadge, updateBadge } from '@/store/updates-hub/updatesHubSlice';
import { NumericBadgeKey } from '@/types/updates-hub';
import { toastService } from '@/services/toast.service';
import { PageHint } from '@/components/common/PageHint';

function getNewsBadgeKey(): NumericBadgeKey {
  return localStorage.getItem('sidebar-mode') === 'expert' ? 'newsExpert' : 'news';
}

export const NewsPage: React.FC = () => {
  const [news, setNews] = useState<PublicNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(9); // 3x3 grid
  const [selectedNews, setSelectedNews] = useState<PublicNewsItem | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await newsService.getNews(page, limit);
      setNews(response.data);
      // Calculate total pages based on total items
      setTotalPages(Math.ceil(response.meta.total / limit));
    } catch (error) {
      console.error('Failed to load news', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [page]);

  const handleOpenNews = async (item: PublicNewsItem) => {
    // Set selected news immediately with list data (for fast UI feedback)
    setSelectedNews(item);
    setLoadingDetails(true);

    // If news is unread, mark it as read locally and update global count
    if (item.isRead === false) {
      // Update local list state
      setNews(prev => prev.map(n => n.id === item.id ? { ...n, isRead: true } : n));
      // Update global unread count (Legacy)
      dispatch(decrementUnreadCount());
      // Update global unread count (Updates Hub)
      dispatch(decrementBadge(getNewsBadgeKey()));
      
      // We don't need to call markAsRead explicitly because getNewsById (findOne) on backend 
      // automatically marks it as read. But if we want to be sure or if backend logic changes:
      // await newsService.markAsRead(item.id); 
    }

    try {
      // Fetch full details (this also marks as read on backend usually)
      const fullDetails = await newsService.getNewsById(item.id);
      setSelectedNews(fullDetails);
    } catch (error) {
      console.error('Failed to load news details', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseNews = () => {
    setSelectedNews(null);
    setLoadingDetails(false);
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await dispatch(markAllNewsAsRead()).unwrap();
      // Update local state to show all as read
      setNews(prev => prev.map(n => ({ ...n, isRead: true })));
      // Update global unread count (Updates Hub)
      dispatch(updateBadge({ key: getNewsBadgeKey(), value: 0 }));
      toastService.success('Всі новини позначено як прочитані');
    } catch (error) {
      console.error('Failed to mark all as read', error);
      toastService.error('Не вдалося позначити новини як прочитані');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handlePageChange = (value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (value: number) => {
    setLimit(value);
    setPage(1);
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg" data-i-doc={PageIndices.NEWS}>
        <PageHint iDoc={PageIndices.NEWS} />
        <Box mb={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DoneAll />}
            onClick={handleMarkAllRead}
            disabled={markingAllRead || loading}
          >
            Позначити всі як прочитані
          </Button>
        </Box>

        {loading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" height={30} width="80%" />
                    <Skeleton variant="text" height={20} width="40%" />
                    <Box mt={2}>
                      <Skeleton variant="text" height={60} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : news.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {news.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    {item.isRead === false && (
                      <Chip 
                        label="Нове" 
                        color="error" 
                        size="small" 
                        icon={<FiberNew />}
                        sx={{ 
                          position: 'absolute', 
                          top: 10, 
                          right: 10, 
                          zIndex: 1,
                          fontWeight: 'bold'
                        }} 
                      />
                    )}
                    <CardActionArea 
                      onClick={() => handleOpenNews(item)}
                      sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={getImageUrl(item.coverImageUrl)}
                        alt={item.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ flexGrow: 1, width: '100%' }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {item.publishedAt 
                              ? format(new Date(item.publishedAt), 'd MMMM yyyy', { locale: uk })
                              : format(new Date(item.createdAt), 'd MMMM yyyy', { locale: uk })
                            }
                          </Typography>
                        </Box>
                        
                        <Typography 
                          gutterBottom 
                          variant="h6" 
                          component="div" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.3,
                            height: '2.6em',
                            fontWeight: item.isRead === false ? 'bold' : 'normal'
                          }}
                        >
                          {item.title}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {item.content}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <UniversalPagination
              page={page}
              count={totalPages}
              limit={limit}
              limitOptions={[9, 18, 36]}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              storageKey="pagination:news"
              sx={{ mt: 4 }}
            />
          </>
        ) : (
          <Box textAlign="center" py={10}>
            <Typography variant="h6" color="text.secondary">
              Наразі новин немає
            </Typography>
          </Box>
        )}

        {/* News Detail Modal */}
        <Dialog
          open={!!selectedNews}
          onClose={handleCloseNews}
          scroll="paper"
          maxWidth="md"
          fullWidth
          fullScreen={fullScreen}
        >
          {selectedNews && (
            <>
              <Box position="relative">
                <CardMedia
                  component="img"
                  height={fullScreen ? "250" : "400"}
                  image={getImageUrl(selectedNews.coverImageUrl)}
                  alt={selectedNews.title}
                  sx={{ objectFit: 'cover' }}
                />
                <IconButton
                  aria-label="close"
                  onClick={handleCloseNews}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
              
              <DialogTitle sx={{ pb: 1, pt: 3 }}>
                <Typography variant="h5" component="div" fontWeight="bold">
                  {selectedNews.title}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Chip 
                    icon={<CalendarToday sx={{ fontSize: '16px !important' }} />} 
                    label={selectedNews.publishedAt 
                      ? format(new Date(selectedNews.publishedAt), 'd MMMM yyyy', { locale: uk })
                      : format(new Date(selectedNews.createdAt), 'd MMMM yyyy', { locale: uk })
                    }
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </DialogTitle>
              
              <DialogContent dividers>
                {loadingDetails ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      lineHeight: 1.8,
                      color: 'text.primary'
                    }}
                  >
                    {selectedNews.content}
                  </Typography>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};
